import React, { useState, useEffect } from 'react';
import { Crown, Check, Lock, MessageCircle, Eye, Search, Users, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isUcanApp, isApplePlatform, sendUcanNativeMessage } from '@/lib/ucanApp';

const SubscriptionCard = ({ userType = 'client', hasSubscription = false, onSubscriptionChange }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  // Promo code states
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    loadPlan();
  }, [userType]);

  const loadPlan = async () => {
    try {
      const role = userType === 'client' ? 'client' : 'provider';
      const res = await api.get(`/subscription/plans?role=${role}`);
      const activePlan = res.data.find(p => p.active) || res.data[0];
      setPlan(activePlan);
    } catch (error) {
      console.error('Error loading plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const validatePromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Ingresa un código');
      return;
    }
    setValidatingPromo(true);
    try {
      const res = await api.post('/promo-codes/validate', { code: promoCode.trim() });
      setAppliedPromo(res.data);
      toast.success(`¡Código aplicado! ${res.data.description || ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido');
      setAppliedPromo(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  const getFinalPrice = () => {
    if (!plan) return 0;
    const original = plan.price_clp;
    if (!appliedPromo) return original;
    if (appliedPromo.discount_type === 'percentage') {
      return Math.round(original * (1 - appliedPromo.discount_value / 100));
    }
    return Math.max(0, original - appliedPromo.discount_value);
  };

  const handleSubscribe = async () => {
    if (!plan) return;

    const isIOSApp = isUcanApp();
    const isAppleSystem = isApplePlatform();

    /*
     * APP U-CAN EN IOS:
     * abre Apple IAP mediante el puente nativo.
     */
    if (isIOSApp) {
      if (!sendUcanNativeMessage({
          type: 'APPLE_SUBSCRIPTION',
          plan_id: plan.plan_id,
          user_type: userType,
        })) {
        toast.error('No se pudo conectar con Apple. Cierra y vuelve a abrir la aplicación U-CAN.');
      }
      return;
    }

    /*
     * NAVEGADOR EN CUALQUIER DISPOSITIVO APPLE:
     * nunca permitimos Mercado Pago.
     *
     * StoreKit solamente puede abrirse desde la app nativa U-CAN.
     */
    if (isAppleSystem) {
      toast.info(
        'U-CAN Premium utiliza suscripción Apple en dispositivos Apple.'
      );
      return;
    }

    /*
     * ANDROID / WINDOWS / LINUX WEB:
     * Mercado Pago normal.
     */
    setSubscribing(true);

    try {
      const payload = {
        plan_id: plan.plan_id,
      };

      if (appliedPromo) {
        payload.promo_code = appliedPromo.code;
      }

      const response = await api.post(
        '/subscription/create-payment',
        payload
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.sandbox_url) {
        window.location.href = response.data.sandbox_url;
      } else {
        toast.error('Error al crear el pago');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Error al procesar suscripción'
      );
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-4 border-2 border-dashed border-gray-200">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-100 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  const isIOSApp = isUcanApp();

  // Cualquier iPhone/iPad debe usar la experiencia visual Apple,
  // tanto dentro de la app como desde Safari/Chrome.
  const isAppleSystem = isApplePlatform();

  if (hasSubscription) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-4 border-2 border-green-200" data-testid="subscription-active">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Crown className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-green-800">Cuenta Premium Activa</h3>
            <p className="text-sm text-green-600">Tienes acceso completo</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {userType === 'client' ? (
            <>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <Eye className="w-3 h-3" /> Ver datos de contacto
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> Mensajería
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <Search className="w-3 h-3" /> Contactar cuidadores
              </span>
            </>
          ) : (
            <>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <Users className="w-3 h-3" /> Ver solicitudes de clientes
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> Enviar propuestas
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <Crown className="w-3 h-3" /> SOS Emergencia
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-4 border-2 border-[#E6202E]" data-testid="subscription-cta">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#E6202E]" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">
                {userType === 'client' ? '¡Desbloquea el contacto!' : '¡Accede a más clientes!'}
              </h3>
              {!isAppleSystem && appliedPromo ? (
                <div>
                  <p className="text-sm text-gray-400 line-through">
                    ${plan?.price_clp?.toLocaleString('es-CL') || '9.990'}/mes
                  </p>
                  <p className="text-2xl font-bold text-green-600" data-testid="discounted-price">
                    ${getFinalPrice().toLocaleString('es-CL')}/mes
                  </p>
                </div>
              ) : (
                <p className="text-2xl font-bold text-[#E6202E]">
                  ${plan?.price_clp?.toLocaleString('es-CL') || '9.990'}/mes
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {userType === 'client' ? (
              <>
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span>Actualmente no puedes ver datos de contacto</span>
                </p>
                <p className="text-sm font-medium text-gray-800">Premium incluye:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Acceso prioritario a cuidadores destacados
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Enviar mensajes directos
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Contactar cuidadores ilimitadamente
                  </li>
                </ul>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span>Puedes responder mensajes, pero no enviar propuestas</span>
                </p>
                <p className="text-sm font-medium text-gray-800">Con Premium podras:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Ver solicitudes de clientes que buscan cuidador
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Enviar propuestas a clientes directamente
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Acceder al panel de oportunidades
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Boton SOS emergencia veterinaria
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#E6202E]" />
                    Perfil destacado en resultados de busqueda
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="w-full sm:w-auto">
          <Button
            onClick={handleSubscribe}
            disabled={subscribing}
            className="w-full sm:w-auto px-4 py-6 text-base font-semibold bg-[#E6202E] hover:bg-[#D31522]"
            data-testid="subscribe-now-btn"
          >
            {subscribing ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Procesando...
              </span>
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" />
                {isAppleSystem ? 'Suscribirme con Apple' : 'Suscribirme'}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500 leading-relaxed">
          {userType === 'client'
            ? 'U-CAN Premium Cliente'
            : 'U-CAN Premium Cuidadores'} · Suscripción mensual con renovación automática.
          Puedes cancelar en cualquier momento desde tu cuenta de Apple.
        </p>

        <div className="mt-2 flex items-center justify-center gap-2 text-xs">
          <a
            href="/terminos"
            className="text-[#E6202E] hover:underline font-medium"
          >
            Términos de Uso (EULA)
          </a>

          <span className="text-gray-300">·</span>

          <a
            href="/privacidad"
            className="text-[#E6202E] hover:underline font-medium"
          >
            Política de Privacidad
          </a>
        </div>
      </div>

      {/* Promo codes are available only on the web.
          Apple requires iOS subscriptions to use App Store mechanisms. */}
      {!isAppleSystem && (
      <div
        className="mt-6 pt-5 border-t-2 border-dashed border-red-200"
      >
        {!showPromoInput && !appliedPromo ? (
          <button
            type="button"
            onClick={() => setShowPromoInput(true)}
            className="w-full group flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all"
            data-testid="show-promo-input"
          >
            <div className="w-10 h-10 bg-white/25 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <Tag className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-white text-base leading-tight">Código promocional</p>
              <p className="text-xs text-white/90">Haz click aquí para aplicar tu descuento</p>
            </div>
          </button>
        ) : appliedPromo ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 border-2 border-green-300 rounded-xl" data-testid="applied-promo">
            <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
              <Tag className="w-5 h-5 text-green-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-green-800">¡Código aplicado!</p>
              <p className="text-sm text-green-700">
                <strong>{appliedPromo.code}</strong> · {appliedPromo.discount_type === 'percentage'
                  ? `${appliedPromo.discount_value}% de descuento`
                  : `$${appliedPromo.discount_value.toLocaleString('es-CL')} de descuento`}
              </p>
            </div>
            <button onClick={removePromo} className="text-green-700 hover:text-green-900 p-2" aria-label="Quitar código">
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
            <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Ingresa tu código promocional
            </p>
            <div className="space-y-2">
              <Input
                placeholder="Ej: BIENVENIDA10"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="w-full bg-white border-amber-300 focus-visible:ring-amber-500"
                data-testid="promo-input"
                disabled={validatingPromo}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={validatePromo}
                  disabled={validatingPromo || !promoCode.trim()}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold"
                  data-testid="apply-promo-btn"
                >
                  {validatingPromo ? '...' : 'Aplicar'}
                </Button>
                <Button
                  type="button"
                  onClick={() => { setShowPromoInput(false); setPromoCode(''); }}
                  variant="ghost"
                  className="w-11 shrink-0"
                  size="sm"
                  aria-label="Cancelar"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
